/**
 * CourseAuthorAutocomplete
 *
 * Picker for a Course's instructor. Backed by the author directory in the
 * lookups slice (see `features/lookups/lookupsSlice.ts`) of reusable
 * instructor templates — each carrying name, credentials, bio, portrait.
 *
 * Flow:
 *   1. Search-as-you-type over existing instructors.
 *   2. Type a name that doesn't exist → "Add new instructor" option
 *      opens the InstructorTemplateDialog so admins fill in the full
 *      template (credentials/bio/portrait) instead of just the name.
 *   3. Picked instructor renders as a green confirmation pill below,
 *      with portrait, name + credentials, an "Edit template" button,
 *      and a clear (✕) button.
 */

import { useMemo, useState } from 'react';
import { Autocomplete, Avatar, Box, Button, IconButton, TextField, Typography } from '../ui';
import { CheckCircleIcon, CloseIcon, EditIcon } from '../icons';
import { useSelector } from 'react-redux';
import { CourseAuthor, SaveCourseAuthorRequest } from '../../apis/api-client';
import { useAppDispatch } from '../../stores';
import {
  createAuthor,
  selectAllAuthors,
  updateAuthor,
  useEnsureAuthors,
} from '../../features/lookups/lookupsSlice';
import { getPortraitObjectPosition } from '../../utils/courseAuthor';
import InstructorTemplateDialog from './InstructorTemplateDialog';

interface CourseAuthorAutocompleteProps {
  value: CourseAuthor | null;
  onChange: (next: CourseAuthor | null) => void;
}

// Either an existing author or a "create new" marker the autocomplete
// surfaces when the typed text doesn't match anything in the directory.
type Option = CourseAuthor | { id: '__new__'; name: string; isNew: true };

export default function CourseAuthorAutocomplete({ value, onChange }: CourseAuthorAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');

  // Template dialog state — used for both "add new" (no author, seeded
  // by typed input) and "edit existing" (author = the current value).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAuthor, setDialogAuthor] = useState<CourseAuthor | null>(null);
  const [dialogSeedName, setDialogSeedName] = useState('');

  // Shared, account-wide directory (fetched once). Mutations below dispatch
  // through the slice so the dropdown reflects adds/edits.
  const dispatch = useAppDispatch();
  useEnsureAuthors();
  const authors = useSelector(selectAllAuthors);

  const options = useMemo<Option[]>(() => {
    const q = inputValue.trim().toLowerCase();
    const matches = q ? authors.filter((a) => (a.name ?? '').toLowerCase().includes(q)) : authors;
    const exact = q && authors.some((a) => (a.name ?? '').toLowerCase() === q);
    if (q && !exact) {
      return [...matches, { id: '__new__', name: inputValue.trim(), isNew: true }];
    }
    return matches;
  }, [authors, inputValue]);

  const openAddDialog = (seedName: string) => {
    setDialogAuthor(null);
    setDialogSeedName(seedName);
    setDialogOpen(true);
  };

  const openEditDialog = (author: CourseAuthor) => {
    setDialogAuthor(author);
    setDialogSeedName('');
    setDialogOpen(true);
  };

  const handleDialogSave = async (patch: SaveCourseAuthorRequest & { name: string }) => {
    if (dialogAuthor?.id != null) {
      // Edit existing — patch fields and propagate the refreshed
      // record to the upstream form.
      const updated = await dispatch(
        updateAuthor({ id: dialogAuthor.id, request: patch }),
      ).unwrap();
      setDialogOpen(false);
      if (value?.id === updated.id) onChange(updated);
    } else {
      // Add new.
      const created = await dispatch(createAuthor(patch)).unwrap();
      setDialogOpen(false);
      onChange(created);
      setInputValue('');
    }
  };

  const handleChange = (_e: any, next: Option | string | null) => {
    if (!next) {
      onChange(null);
      return;
    }
    if (typeof next === 'string') {
      // freeSolo handed us a raw string (Enter on unmatched text).
      // Bounce through the template dialog so the admin can fill in
      // the rest before the instructor lands in the directory.
      openAddDialog(next);
      return;
    }
    if ((next as any).isNew) {
      openAddDialog((next as any).name);
      return;
    }
    onChange(next as CourseAuthor);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Autocomplete<Option, false, false, true>
        freeSolo
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        options={options}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name ?? '')}
        isOptionEqualToValue={(opt, val) =>
          typeof opt !== 'string' && typeof val !== 'string' && opt.id === (val as any).id
        }
        filterOptions={(x) => x}
        renderOption={(props, option) => {
          if ((option as any).isNew) {
            return (
              <Box
                component="li"
                {...props}
                key="__new__"
                sx={{ display: 'flex !important', justifyContent: 'space-between' }}
              >
                <Typography variant="body2">
                  Add new instructor <strong>"{(option as any).name}"</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Opens template
                </Typography>
              </Box>
            );
          }
          const opt = option as CourseAuthor;
          return (
            <Box
              component="li"
              {...props}
              key={opt.id}
              sx={{ display: 'flex !important', alignItems: 'center', gap: 1.25 }}
            >
              <Avatar
                src={opt.portraitUrl || undefined}
                imgProps={{ style: { objectPosition: getPortraitObjectPosition(opt) } }}
                sx={{ width: 32, height: 32, fontSize: '0.85rem' }}
              >
                {(opt.name ?? '?').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {opt.name}
                </Typography>
                {opt.credentials && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {opt.credentials}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Course instructor"
            placeholder="Search instructors or type a new name"
            helperText="Pick a saved instructor template, or add a new one — credentials, bio, and portrait carry to every course they teach."
            fullWidth
          />
        )}
      />

      {/* Confirmation pill — clear visual signal that an instructor
          has been picked, plus inline access to the edit template. */}
      {value && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'rgba(46,125,50,0.35)',
            bgcolor: 'rgba(46,125,50,0.08)',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
          <Avatar
            src={value.portraitUrl || undefined}
            imgProps={{ style: { objectPosition: getPortraitObjectPosition(value) } }}
            sx={{ width: 36, height: 36, flexShrink: 0 }}
          >
            {(value.name ?? '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {value.name}
              {value.credentials && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.75,
                    py: 0.1,
                    borderRadius: 999,
                    bgcolor: 'rgba(46,125,50,0.18)',
                    color: 'success.main',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                  }}
                >
                  {value.credentials}
                </Box>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Instructor assigned · template fills every course they teach
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            onClick={() => openEditDialog(value)}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Edit
          </Button>
          <IconButton
            size="small"
            onClick={() => onChange(null)}
            aria-label="Remove instructor"
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <InstructorTemplateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        author={dialogAuthor}
        initialName={dialogSeedName}
        onSave={handleDialogSave}
      />
    </Box>
  );
}
